import type { RequestConfig } from "@app/config/axios/http";
import {
    wsInplantsServices,
    type WsInplantContent,
    type WsInplantExtends,
} from "@src/businessModules/WorkShop/api/wsInplants";
import { lotsServices, type ApiStockLot } from "../lots";
import { storePlacesApi, type SpMoving, type StorePlaceRecommendedCell } from "../storePlaces";
import type {
    FinishedGoodsReceipt,
    FinishedGoodsReceiptContent,
    FinishedGoodsReceiptContentExtends,
    FinishedGoodsReceiptFilter,
} from "./types";

/** Ключ кэша рекомендаций: заказ + ТМЦ + подразделение. */
type RecommendedCellsKey = `${number}:${number}:${number}`;

/** Сервисный слой сценария приемки готовой продукции на СГП. */
export const finishedGoodsReceiptsServices = {
    /** Загружает список ВЗН УП для приемки СГП. */
    load: async (
        params: {
            flt?: FinishedGoodsReceiptFilter;
            include?: Array<keyof WsInplantExtends>;
            includeBo?: Array<"State">;
        },
        config?: RequestConfig,
    ): Promise<FinishedGoodsReceipt[]> => {
        return wsInplantsServices.load(params, config);
    },

    /** Возвращает одну ВЗН УП для карточки приемки СГП. */
    loadOne: async (
        params: {
            flt: FinishedGoodsReceiptFilter;
            include?: Array<keyof WsInplantExtends>;
            includeBo?: Array<"State">;
        },
        config?: RequestConfig,
    ): Promise<FinishedGoodsReceipt | null> => {
        return wsInplantsServices.loadOne(params, config);
    },

    content: {
        /**
         * Загружает строки ВЗН УП и догружает данные размещения готовой продукции. \
         * Строки с `LotCode = 0` отфильтровываются (нет партионного учёта). \
         * `ArrivalLot` — партия приходного движения по `ArrivalSdbCode` (`lots.loadByFilter.Codes`). \
         * Рекомендации — `getRecommendedCellsForOrder` по ключу заказ/ТМЦ/подразделение. \
         * Фактические ячейки — `spMoving` по `ArrivalSdbCode` (пустой ответ = ещё не размещали).
         * @param params фильтр состава, include и код склада-получателя
         * @param config опции HTTP-запроса
         */
        loadForReceipt: async (
            params: {
                flt?: { WsInplantCode?: number; Codes?: number[] };
                include?: Array<"LeaveCard" | "Order" | "ArrivalLot">;
                includeLeaveCard?: Array<"Stockobj" | "Measure">;
                /** Код склада СГП (получатель ВЗН) для рекомендаций ячеек. */
                divisionCode: number;
                recommendedCellsLimit?: number;
            },
            config?: RequestConfig,
        ): Promise<FinishedGoodsReceiptContent[]> => {
            const { recommendedCellsLimit = 3, divisionCode, include, ...loadParams } = params;
            const includeArrivalLot = include?.includes("ArrivalLot") === true;
            const wsInplantIncludes = include?.filter(
                (key): key is "LeaveCard" | "Order" => key === "LeaveCard" || key === "Order",
            );
            const rawItems = await wsInplantsServices.content.load(
                {
                    ...loadParams,
                    include: wsInplantIncludes,
                },
                config,
            );
            const items = rawItems.filter((item) => isValidCode(item.LotCode));

            const arrivalSdbCodes = collectValidCodes(items.map((item) => item.ArrivalSdbCode));

            const arrivalLots = new Map<number, ApiStockLot>();
            const recommendedCells = new Map<RecommendedCellsKey, StorePlaceRecommendedCell[]>();
            const spMovings = new Map<number, SpMoving[]>();
            const promises: Promise<unknown>[] = [];

            if (includeArrivalLot && arrivalSdbCodes.length > 0) {
                promises.push(
                    lotsServices.load({ flt: { Codes: arrivalSdbCodes } }, config).then((loadedLots) => {
                        loadedLots.forEach((lot) => {
                            if (isValidCode(lot.LotCode)) {
                                arrivalLots.set(lot.LotCode, lot);
                            }
                        });
                    }),
                );
            }

            if (isValidCode(divisionCode)) {
                const recommendationKeys = collectRecommendationKeys(items, divisionCode);
                promises.push(
                    Promise.all(
                        recommendationKeys.map(async ({ key, orderCode, stockobjCode }) => {
                            const cells = await storePlacesApi.getRecommendedCellsForOrder(
                                {
                                    divisionCode,
                                    orderCode,
                                    stockobjCode,
                                    nCellsToFind: recommendedCellsLimit,
                                },
                                config,
                            );
                            recommendedCells.set(key, cells);
                        }),
                    ),
                );
            }

            if (arrivalSdbCodes.length > 0) {
                promises.push(
                    storePlacesApi.spMoving
                        .loadByFilter(
                            {
                                filter: {
                                    SdbCodes: arrivalSdbCodes,
                                    OnlyNotInv: true,
                                },
                            },
                            config,
                        )
                        .then((movements) => {
                            movements.forEach((movement) => {
                                if (!isValidCode(movement.SdbCode)) {
                                    return;
                                }

                                const itemsBySdb = spMovings.get(movement.SdbCode) ?? [];
                                itemsBySdb.push(movement);
                                spMovings.set(movement.SdbCode, itemsBySdb);
                            });
                        }),
                );
            }

            await Promise.all(promises);

            if (arrivalSdbCodes.length > 0) {
                await enrichSpMovingsWithStorePlaces(spMovings, config);
            }

            return items.map((item) => {
                const stockObj = item.LeaveCard?.Stockobj;
                const recommendationKey =
                    isValidCode(divisionCode) && isValidCode(item.OrderCode) && isValidCode(stockObj?.Code)
                        ? buildRecommendedCellsKey(item.OrderCode, stockObj.Code, divisionCode)
                        : null;

                return serializeReceiptContent(item, {
                    ArrivalLot:
                        includeArrivalLot && isValidCode(item.ArrivalSdbCode)
                            ? arrivalLots.get(item.ArrivalSdbCode)
                            : undefined,
                    StockObj: stockObj,
                    Measure: item.LeaveCard?.Measure,
                    RecommendedCells: recommendationKey != null ? recommendedCells.get(recommendationKey) : undefined,
                    SpMovings: isValidCode(item.ArrivalSdbCode) ? (spMovings.get(item.ArrivalSdbCode) ?? []) : undefined,
                });
            });
        },
    },
} as const;

function serializeReceiptContent(
    content: WsInplantContent,
    includeData: Partial<FinishedGoodsReceiptContentExtends>,
): FinishedGoodsReceiptContent {
    return {
        ...content,
        StockObj: includeData.StockObj,
        Measure: includeData.Measure,
        ArrivalLot: includeData.ArrivalLot,
        RecommendedCells: includeData.RecommendedCells,
        SpMovings: includeData.SpMovings,
    };
}

function collectRecommendationKeys(
    items: WsInplantContent[],
    divisionCode: number,
): Array<{ key: RecommendedCellsKey; orderCode: number; stockobjCode: number }> {
    const keys = new Map<RecommendedCellsKey, { orderCode: number; stockobjCode: number }>();

    items.forEach((item) => {
        const stockobjCode = item.LeaveCard?.Stockobj?.Code;
        if (!isValidCode(item.OrderCode) || !isValidCode(stockobjCode)) {
            return;
        }

        const key = buildRecommendedCellsKey(item.OrderCode, stockobjCode, divisionCode);
        if (!keys.has(key)) {
            keys.set(key, { orderCode: item.OrderCode, stockobjCode });
        }
    });

    return [...keys.entries()].map(([key, value]) => ({ key, ...value }));
}

function buildRecommendedCellsKey(orderCode: number, stockobjCode: number, divisionCode: number): RecommendedCellsKey {
    return `${orderCode}:${stockobjCode}:${divisionCode}`;
}

async function enrichSpMovingsWithStorePlaces(spMovings: Map<number, SpMoving[]>, config?: RequestConfig): Promise<void> {
    const storePlaceCodes = new Set<number>();

    spMovings.forEach((movements) => {
        movements.forEach((movement) => {
            if (isValidCode(movement.SpCode)) {
                storePlaceCodes.add(movement.SpCode);
            }
        });
    });

    if (storePlaceCodes.size === 0) {
        return;
    }

    const storePlaces = new Map<number, NonNullable<SpMoving["StorePlace"]>>();
    const loaded = await storePlacesApi.load({ filter: { Codes: [...storePlaceCodes] } }, config);
    loaded.forEach((storePlace) => storePlaces.set(storePlace.Code, storePlace));

    spMovings.forEach((movements, sdbCode) => {
        spMovings.set(
            sdbCode,
            movements.map((movement) => ({
                ...movement,
                StorePlace: isValidCode(movement.SpCode) ? storePlaces.get(movement.SpCode) : undefined,
            })),
        );
    });
}

function collectValidCodes(codes: number[]): number[] {
    return [...new Set(codes.filter(isValidCode))];
}

function isValidCode(code: number | null | undefined): code is number {
    return code != null && code !== 0;
}
